//------------------------------------------------------------------------------
// Node Modules ----------------------------------------------------------------
import React from 'react';
import classNames from 'classnames';
import ReactPaginate from 'react-paginate';
import { withTranslation } from 'react-i18next';
//------------------------------------------------------------------------------
// Style -----------------------------------------------------------------------
import styles from './index.module.scss';
//------------------------------------------------------------------------------
// My Components ---------------------------------------------------------------
import { Badge } from '@cmp/common';
import { ChevronLeftIcon, ChevronRightIcon } from '@cmp/images';
//------------------------------------------------------------------------------
// Helpers & Constants ---------------------------------------------------------
import { referralFormatter } from '@helpers/formatter';
import { StoreConfigContext } from '@helpers/storeConfigContext';
const HistoryPageCount = 5;
//------------------------------------------------------------------------------
// API -------------------------------------------------------------------------
import { History as RecommendationHistoryRequest } from '@api/endpoints/get/recommendation';
import { currencyFormatter } from '@helpers/formatter';
//------------------------------------------------------------------------------
// React Class -----------------------------------------------------------------
class History extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      currentPage: 0,
    };
  }

  itemComponent({ id, petName, fullName, date, amount, status, source }) {
    const { onClick, t } = this.props;
    const {
      storeConfig: { currencySymbol, language },
    } = this.context;

    return (
      <div
        className={styles.Item}
        tabIndex={'0'}
        onClick={() => onClick(id)}
        key={id}
      >
        <span className={styles.FullName}>{petName || '–'}</span>
        <span className={styles.FullName}>{fullName || '–'}</span>
        <span className={styles.date}>{date}</span>
        <span className={styles.amount}>
          {currencyFormatter.priceWithCurrency(
            amount,
            currencySymbol,
            language
          )}
        </span>
        <Badge status={status} className={styles.Badge} />
        {this.showSourceColumn() && (
          <span className={styles.FullName}>{t(source.i18nKey)}</span>
        )}
      </div>
    );
  }

  componentDidUpdate(prevProps) {
    // Ideally the pagination component wouldn't be controlled by History,
    // and all the changes/refetches would come from the top

    const { searchValue, sortBy } = this.props;

    if (searchValue !== prevProps.searchValue || sortBy !== prevProps.sortBy) {
      this.setState({ currentPage: 0 }, this.refetch);
    }
  }

  params() {
    const { currentPage } = this.state;
    const { searchValue, sortBy, startDate, endDate } = this.props;

    const params = {
      // ReactPaginate has a zero-based page count, while our API doesn't
      page: currentPage + 1,
      limit: HistoryPageCount,
      sentOnly: true,
      isHistory: true,
    };

    if (searchValue && searchValue.length > 0) {
      params.search = searchValue;
    }

    if (sortBy && sortBy.length > 0) {
      params.sort = sortBy;
    }

    if (startDate && endDate) {
      params.rangeStart = startDate
        .clone()
        .utc(true)
        .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
        .toISOString();
      // date passes as 2023-10-22T11:00:00.000Z so we need to add 1 day to get the end of the day
      params.rangeEnd = endDate
        .clone()
        .utc(true)
        .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
        .add(1, 'd')
        .toISOString();
      params.rangeCol = 'sentAt';
    }

    return params;
  }

  render() {
    const { currentPage } = this.state;
    const {
      storeConfig: { language },
    } = this.context;

    const { className, onChangeRefetch, t } = this.props;

    const componentClasses = classNames(styles.History, {
      [className]: className,
    });

    return (
      <RecommendationHistoryRequest params={this.params()}>
        {({ data, refetch }) => {
          this.refetch = refetch;
          onChangeRefetch(refetch);

          const { referrals, total } = data || {};
          if (!data || !language || !this.context.storeConfig.language)
            return '';

          return (
            <div className={componentClasses}>
              {/* mobile */}
              {referrals.map((referral, i) => {
                if (!this.context.storeConfig.language) return '';

                const formattedCreatedAt = referralFormatter.formattedCreatedAt(
                  referral,
                  { locale: this.context.storeConfig.language }
                );

                const {
                  storeConfig: { currencySymbol, language },
                } = this.context;
                const { onClick } = this.props;

                return (
                  <React.Fragment key={i}>
                    <div
                      className={styles.RecommendationMobile}
                      onClick={() => onClick(referral.id)}
                      key={referral.id}
                    >
                      <div className={styles.HeaderItemM}>
                        {t('dashboard_RecommendationHistoryPetNameHeader')}
                      </div>
                      <div className={styles.FullNameM}>
                        {referral.refDetails?.petName || '–'}
                      </div>
                      <div className={styles.HeaderItemM}>
                        {t('dashboard_RecommendationHistoryFullNameHeader')}
                      </div>
                      <div className={styles.FullNameM}>
                        {referral.consumerName || '–'}
                      </div>
                      <Badge
                        status={referralFormatter.status(referral)}
                        className={styles.BadgeM}
                      />
                      <div className={styles.DateAmount}>
                        <div className={styles.DateM}>
                          {t('dashboard_RecommendationHistoryDateHeader')}
                          <div className={styles.DateItem}>
                            {formattedCreatedAt}
                          </div>
                        </div>
                        <div className={styles.AmountHeaderM}>
                          {t('dashboard_RecommendationHistoryAmountHeader')}
                          <div className={styles.Amountm}>
                            {currencyFormatter.priceWithCurrency(
                              referral.totalCommissionValue,
                              currencySymbol,
                              language
                            )}
                          </div>
                        </div>
                      </div>
                      {this.showSourceColumn() && (
                        <>
                          <div className={styles.HeaderItemM}>
                            {t('dashboard_RecommendationSourceHeader')}
                          </div>
                          <div className={styles.FullNameM}>
                            {t(referralFormatter.source(referral).i18nKey)}
                          </div>
                        </>
                      )}
                    </div>
                  </React.Fragment>
                );
              })}

              {/* eo mobile */}
              <div className={styles.Table}>
                <div className={styles.TableHeader}>
                  <span
                    className={classNames(
                      styles.FullNameHeader,
                      styles.HeaderItem
                    )}
                  >
                    {t('dashboard_RecommendationHistoryPetNameHeader')}
                  </span>
                  <span
                    className={classNames(
                      styles.FullNameHeader,
                      styles.HeaderItem
                    )}
                  >
                    {t('dashboard_RecommendationHistoryFullNameHeader')}
                  </span>
                  <span
                    className={classNames(styles.DateHeader, styles.HeaderItem)}
                  >
                    {t('dashboard_RecommendationHistoryDateHeader')}
                  </span>
                  <span
                    className={classNames(
                      styles.AmountHeader,
                      styles.HeaderItem
                    )}
                  >
                    {t('dashboard_RecommendationHistoryAmountHeader')}
                  </span>
                  <span
                    className={classNames(
                      styles.AmountHeader,
                      styles.HeaderItem
                    )}
                  >
                    {t('dashboard_RecommendationHistoryStatusHeader')}
                  </span>
                  {this.showSourceColumn() && (
                    <span className={styles.HeaderItem}>
                      {t('dashboard_RecommendationSourceHeader')}
                    </span>
                  )}
                </div>

                {referrals.map((referral) => {
                  const formattedCreatedAt =
                    referralFormatter.formattedCreatedAt(referral, {
                      locale: language,
                    });

                  return this.itemComponent({
                    id: referral.id,
                    petName: referral.refDetails?.petName,
                    fullName: referral.consumerName,
                    date: formattedCreatedAt,
                    amount: referral.totalCommissionValue,
                    status: referralFormatter.status(referral),
                    source: referralFormatter.source(referral),
                  });
                })}
              </div>
              {(language == 'en' || language == 'ja') && (
                <div className={styles.TaxComissionDisclaimer}>
                  {t('dashboard_TaxComissionDisclaimer')}
                </div>
              )}
              {total > 1 && (
                <ReactPaginate
                  // Basic set up
                  initialPage={currentPage}
                  pageRangeDisplayed={2}
                  marginPagesDisplayed={1}
                  previousLabel={<ChevronLeftIcon />}
                  nextLabel={<ChevronRightIcon />}
                  // Page
                  forcePage={currentPage}
                  onPageChange={({ selected }) => {
                    if (currentPage !== selected) {
                      this.setState({ currentPage: selected }, refetch);
                    }
                  }}
                  disabledClassName={styles.Disabled}
                  pageCount={Math.ceil(total / HistoryPageCount)}
                  // Styling
                  containerClassName={styles.Pagination}
                  activeLinkClassName={styles.PageActive}
                />
              )}
            </div>
          );
        }}
      </RecommendationHistoryRequest>
    );
  }

  showSourceColumn() {
    const {
      featureFlags: { useEUQuickReco, loyaltyProgram },
    } = this.context;
    const appUrl = window.location.href;

    return useEUQuickReco && loyaltyProgram;
  }
}
//------------------------------------------------------------------------------
// Export ----------------------------------------------------------------------
export default withTranslation()(History);
History.contextType = StoreConfigContext;
